import React, { useCallback, useMemo } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Stack, Box, Text } from '@atlaskit/primitives/compiled';

import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';

import { useContentMetricsSearch } from '../../hooks/useContentMetricsSearch.ts';
import { useContentMetricsFilters } from '../../hooks/useContentMetricsFilters.ts';
import { useContentMetrics } from '../../hooks/useContentMetrics.ts';
import { useSpacesForFilter } from '../../hooks/useSpacesForFilter.ts';
import { useOwnersForFilter } from '../../hooks/useOwnersForFilter.ts';

import { ContentMetricsTable } from './ContentMetricsTable.tsx';
import { OverallContentPerformance } from './OverallContentPerformance.tsx';
import { ViewControls } from './ViewControls.tsx';

const i18n = defineMessages({
	description: {
		id: 'performance-insights.content-metrics.description.non-final',
		defaultMessage:
			'Identify content with slower load times. Content performance is based on average load times from the last 7 days. Content marked as slow or average may benefit from optimization. To improve performance, take the recommended actions provided for each content.',
		description:
			'This is a description for the content metrics tab of the performance insights page. It is only viewable by admins and is part of the Confluence Administration section of Confluence. This message should provide an overview of the content metrics tab and how to use it to identify and optimize slow loading content.',
	},
});

const styles = cssMap({
	container: {
		width: '100%',
		marginBlockStart: token('space.300'),
	},
	description: {
		marginBottom: token('space.200'),
	},
	tableSection: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.200'),
		marginTop: token('space.300'),
	},
});

export const ContentMetricsTab: React.FC = () => {
	const { searchText, searchQuery, onSearchChange, clearSearch } = useContentMetricsSearch();
	const {
		selectedTypes,
		onTypeSelectionChange,
		selectedRatings,
		onRatingSelectionChange,
		selectedSpaceIds,
		onSpaceSelectionChange,
		selectedOwnerIds,
		onOwnerSelectionChange,
		clearAllFilters,
	} = useContentMetricsFilters();

	const onClearAll = useCallback(() => {
		clearAllFilters();
		clearSearch();
	}, [clearAllFilters, clearSearch]);

	const filters = useMemo(
		() => ({
			types: selectedTypes,
			ratings: selectedRatings,
			spaceIds: selectedSpaceIds,
			ownerIds: selectedOwnerIds,
		}),
		[selectedTypes, selectedRatings, selectedSpaceIds, selectedOwnerIds],
	);

	const { data: items, loading, error } = useContentMetrics(searchQuery, filters);

	// Spaces are sourced from the Spaces API (not contentPerformanceList) so that
	// icon/emoji data is available and the list is not narrowed by active filters.
	const { spaces } = useSpacesForFilter();

	const { owners } = useOwnersForFilter();

	const hasActiveFilters =
		selectedTypes.length > 0 ||
		selectedRatings.length > 0 ||
		selectedSpaceIds.length > 0 ||
		selectedOwnerIds.length > 0 ||
		searchQuery.trim().length > 0;

	return (
		<Stack xcss={styles.container}>
			<Box xcss={styles.description}>
				<Text>
					<FormattedMessage {...i18n.description} />
				</Text>
			</Box>
			<OverallContentPerformance />
			<Box xcss={styles.tableSection}>
				<ViewControls
					searchText={searchText}
					onSearchChange={onSearchChange}
					selectedTypes={selectedTypes}
					onTypeSelectionChange={onTypeSelectionChange}
					selectedRatings={selectedRatings}
					onRatingSelectionChange={onRatingSelectionChange}
					spaces={spaces}
					selectedSpaceIds={selectedSpaceIds}
					onSpaceSelectionChange={onSpaceSelectionChange}
					owners={owners}
					selectedOwnerIds={selectedOwnerIds}
					onOwnerSelectionChange={onOwnerSelectionChange}
					onClearAll={onClearAll}
				/>
				{/* TODO: Add correct error handling for onError handler */}
				<TransparentErrorBoundary attribution={Attribution.PERFORMANCE_INSIGHTS} onError={() => {}}>
					<ContentMetricsTable
						items={items}
						loading={loading}
						error={error}
						hasActiveFilters={hasActiveFilters}
						spaces={spaces}
					/>
				</TransparentErrorBoundary>
			</Box>
		</Stack>
	);
};
